'use client';

import { JSX, ReactNode } from 'react';
import { useEvent } from 'react-use';

import {
    CollapsibleContent,
    CollapsibleRoot,
    CollapsibleRootProps,
    CollapsibleTrigger,
    Portal,
} from '@videoblocks/design-system';
import { AudioStockItem } from '@videoblocks/search-gateway-client';

import { StockItemContext, StockItemDrawerType, StockItemUserData } from '../../elements/StockItemTypes';
import { DRAWER_EVENTS, ExpandDrawerEvent } from '../../events/DrawerEvents';
import AudioStockItemCard from './AudioStockItemCard';

export interface AudioAltVersionCollapsibleProps extends Omit<CollapsibleRootProps, 'onOpenChange'> {
    currStockItem: AudioStockItem;
    stockItems: AudioStockItem[];
    trigger: ReactNode;
    portalContainer?: HTMLElement | null;
    userData: StockItemUserData;
    // radix-ui incorrectly types onOpenChange as an unbound function, instead of using the arrow-function syntax
    onOpenChange?: (open: boolean) => void;
}

export const AudioAltVersionCollapsible = ({
    currStockItem,
    stockItems = [],
    onOpenChange,
    open,
    trigger,
    portalContainer = null,
    userData,
}: AudioAltVersionCollapsibleProps): JSX.Element => {
    useEvent(DRAWER_EVENTS.EXPAND, (event: ExpandDrawerEvent) => {
        if (currStockItem.id !== event.initialItemId && open) {
            onOpenChange?.(false);
        }
    });

    return (
        <CollapsibleRoot data-testid="audio-alt-version-collapsible-root" onOpenChange={onOpenChange} open={open}>
            <Portal container={portalContainer}>
                <CollapsibleContent data-testid="audio-alt-version-collapsible-content" asChild>
                    <div>
                        {stockItems.map((stockItem) => (
                            <div key={stockItem.id} className="ml-21">
                                <AudioStockItemCard
                                    stockItem={stockItem}
                                    context={StockItemContext.DRAWER}
                                    drawerContext={StockItemDrawerType.ALT_VERSIONS}
                                    userData={userData}
                                />
                            </div>
                        ))}
                    </div>
                </CollapsibleContent>
            </Portal>
            <CollapsibleTrigger asChild>
                <div>{trigger}</div>
            </CollapsibleTrigger>
        </CollapsibleRoot>
    );
};
